import { useEffect, useRef, useState } from "react";

// Persistent canvas-based heart rain that activates when the wrapped section is in view
export const HeartRain = ({ density = 40 }) => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const [active, setActive] = useState(false);
  const heartsRef = useRef([]);
  const rafRef = useRef(null);

  // Observe wrapper for visibility
  useEffect(() => {
    if (!wrapRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => setActive(entry.isIntersecting),
      { threshold: 0.05 }
    );
    obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  // Heart drawing helper
  const drawHeart = (ctx, x, y, size, rotation, color, alpha) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rotation);
    ctx.globalAlpha = alpha;
    ctx.fillStyle = color;
    ctx.beginPath();
    const s = size / 30;
    ctx.moveTo(0, -10 * s);
    ctx.bezierCurveTo(0, -14 * s, -5 * s, -18 * s, -10 * s, -18 * s);
    ctx.bezierCurveTo(-18 * s, -18 * s, -18 * s, -7 * s, -18 * s, -7 * s);
    ctx.bezierCurveTo(-18 * s, 0, -10 * s, 8 * s, 0, 16 * s);
    ctx.bezierCurveTo(10 * s, 8 * s, 18 * s, 0, 18 * s, -7 * s);
    ctx.bezierCurveTo(18 * s, -7 * s, 18 * s, -18 * s, 10 * s, -18 * s);
    ctx.bezierCurveTo(5 * s, -18 * s, 0, -14 * s, 0, -10 * s);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  };

  // Animate
  useEffect(() => {
    if (!active) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      heartsRef.current = [];
      const c = canvasRef.current;
      if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const rect = wrapRef.current.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = rect.width + "px";
      canvas.style.height = rect.height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const colors = ["#F0311B", "#F46555", "#FFFFFF", "#F0311B", "#141518"];

    const spawn = () => {
      const w = canvas.width / dpr;
      const size = 14 + Math.random() * 30;
      heartsRef.current.push({
        x: Math.random() * w,
        y: -30 - Math.random() * 80,
        vy: 1.2 + Math.random() * 2.5,
        vx: (Math.random() - 0.5) * 0.6,
        rotation: (Math.random() - 0.5) * 1.2,
        vr: (Math.random() - 0.5) * 0.04,
        size,
        color: colors[Math.floor(Math.random() * colors.length)],
        alpha: 0.55 + Math.random() * 0.45,
      });
    };

    // Pre-seed some hearts so it doesn't feel empty
    for (let i = 0; i < density; i++) {
      const w = canvas.width / dpr;
      const h = canvas.height / dpr;
      const size = 14 + Math.random() * 30;
      heartsRef.current.push({
        x: Math.random() * w,
        y: Math.random() * h,
        vy: 1.2 + Math.random() * 2.5,
        vx: (Math.random() - 0.5) * 0.6,
        rotation: (Math.random() - 0.5) * 1.2,
        vr: (Math.random() - 0.5) * 0.04,
        size,
        color: colors[Math.floor(Math.random() * colors.length)],
        alpha: 0.55 + Math.random() * 0.45,
      });
    }

    let lastSpawn = 0;
    const loop = (t) => {
      const w = canvas.width / dpr;
      const h = canvas.height / dpr;
      ctx.clearRect(0, 0, w, h);

      // Spawn rate
      if (t - lastSpawn > 60 && heartsRef.current.length < density * 1.5) {
        spawn();
        lastSpawn = t;
      }

      heartsRef.current.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.vr;
        drawHeart(ctx, p.x, p.y, p.size, p.rotation, p.color, p.alpha);
      });

      heartsRef.current = heartsRef.current.filter((p) => p.y < h + 50);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener("resize", resize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [active, density]);

  return (
    <div ref={wrapRef} className="absolute inset-0 pointer-events-none z-10">
      <canvas
        ref={canvasRef}
        data-testid="heart-rain-canvas"
        className="block w-full h-full"
      />
    </div>
  );
};
